// src/components/FolderTree/FolderTree.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FlexGroup, Icon, Skeleton, Text, Tooltip, TreeView } from '@zenqms/zui';
import shareSlice, { fetchSubfolders } from '../../share/share-slice';
import { buildTreeNodes, findShareForFolder } from './buildTreeNodes';
import { ExpiryChip } from './ExpiryChip';
import { measureTextWidth } from '../../utils/measureTextWidth';
import './FolderTree.scss';

/**
 * Effective max label width for a root share row — kept in sync with the same
 * heuristic used for child nodes (buildTreeNodes.js) and the ui workspace's
 * useFolderTree.ts, so tooltip behavior is identical between the two apps.
 */
const ROOT_LABEL_MAX_WIDTH_PX = 200;

export function FolderTree({ onSelect, selectedId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const shares = useSelector((state) => state.share.shares);
  const folderRootMetadata = useSelector((state) => state.share.folderRootMetadata);
  const subfolders = useSelector((state) => state.share.subfolders);
  const expandedNodeIds = useSelector((state) => state.share.expandedNodeIds);
  const loadingNodeIds = useSelector((state) => state.share.loadingNodeIds);
  const isLoading = useSelector((state) => state.share.isLoading);
  const hasLoaded = useSelector((state) => state.share.hasLoaded);
  const loadFailed = useSelector((state) => state.share.loadFailed);

  const expiryByFolderId = Object.fromEntries(shares.map(({ folderId, expiresAt }) => [folderId, expiresAt]));

  const inaccessibleFolderIds = useSelector((state) => state.share.inaccessibleFolderIds);
  const nodes = buildTreeNodes(shares, folderRootMetadata, subfolders, inaccessibleFolderIds);

  function handleExpandedChange(newExpandedIds) {
    const previousSet = new Set(expandedNodeIds);
    const newlyExpanded = newExpandedIds.filter((id) => !previousSet.has(id));
    dispatch(shareSlice.actions.setExpandedNodeIds(newExpandedIds));
    newlyExpanded.forEach((folderId) => {
      if (Object.prototype.hasOwnProperty.call(subfolders, folderId)) return;
      const share = findShareForFolder(folderId, shares, subfolders);
      if (!share) return;
      dispatch(fetchSubfolders({ folderShareId: share.folderShareId, folderId, navigate }));
    });
  }

  if (isLoading || (!hasLoaded && !loadFailed)) {
    return (
      <FlexGroup
        direction="column"
        gap="xs"
        className="folder-tree-skeleton"
        aria-label={formatMessage({ id: 'folderTree.loading', defaultMessage: 'Loading folders\u2026' })}
        aria-busy="true"
      >
        {[1, 2, 3, 4].map((i) => (
          <FlexGroup key={i} gap="sm" alignItems="center" className="folder-tree-skeleton-row">
            <Skeleton variant="circle" width={16} height={16} />
            <Skeleton variant="rectangular" width={`${50 + (i % 3) * 20}%`} height={14} />
          </FlexGroup>
        ))}
      </FlexGroup>
    );
  }

  if (loadFailed && !hasLoaded) {
    return (
      <FlexGroup direction="column" className="folder-tree-empty" role="alert">
        <Text size="md">
          {formatMessage({
            id: 'folderTree.errorBoundaryFallback',
            defaultMessage: 'The folder tree could not be displayed. Please refresh the page.',
          })}
        </Text>
      </FlexGroup>
    );
  }

  if (shares.length === 0 || nodes.length === 0) {
    return (
      <FlexGroup direction="column" className="folder-tree-empty" role="status">
        <Text size="md">
          {formatMessage({ id: 'folderTree.emptyState', defaultMessage: 'You have no shared folders.' })}
        </Text>
      </FlexGroup>
    );
  }

  return (
    <div className="folder-tree-root">
      {nodes.map((rootNode) => {
        const expiresAt = expiryByFolderId[rootNode.id];
        const isExpanded = expandedNodeIds.includes(rootNode.id);
        const isSelected = selectedId === rootNode.id;
        const childNodes = rootNode.children ?? [];

        return (
          <div key={rootNode.id} className="folder-tree-share-group">
            <button
              type="button"
              className={`folder-tree-root-row${isSelected ? ' folder-tree-root-row--selected' : ''}`}
              onClick={() => onSelect && onSelect(rootNode.id)}
            >
              {rootNode.hasChildren ? (
                <span
                  className={`folder-tree-chevron${isExpanded ? ' folder-tree-chevron--expanded' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = isExpanded
                      ? expandedNodeIds.filter((id) => id !== rootNode.id)
                      : [...expandedNodeIds, rootNode.id];
                    handleExpandedChange(next);
                  }}
                  role="button"
                  tabIndex={-1}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" noHover />
                </span>
              ) : (
                <span className="folder-tree-chevron-placeholder" />
              )}

              <Icon name="folder" size="sm" noHover className="folder-tree-folder-icon" />
              {measureTextWidth(rootNode.name) > ROOT_LABEL_MAX_WIDTH_PX ? (
                <Tooltip
                  content={rootNode.name}
                  position="right center"
                  on={['hover', 'focus']}
                  trigger={
                    <span className="folder-tree-root-label" tabIndex={0}>
                      {rootNode.name}
                    </span>
                  }
                />
              ) : (
                <span className="folder-tree-root-label">{rootNode.name}</span>
              )}

              {expiresAt && (
                <span className="folder-tree-chip-slot">
                  <ExpiryChip expiresAt={expiresAt} />
                </span>
              )}
            </button>

            {isExpanded && childNodes.length > 0 && (
              <div className="folder-tree-subtree">
                <TreeView
                  nodes={childNodes}
                  selectionMode="single"
                  expandedIds={expandedNodeIds}
                  onExpandedChange={handleExpandedChange}
                  loadingIds={loadingNodeIds}
                  expandTrigger="row"
                  selectedId={selectedId}
                  allowDeselect={false}
                  onSelect={onSelect}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
